import type { ReactNode } from 'react'
import styles from './Pill.module.css'

export type PillTone =
  | 'neutral'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'indigo'
  | 'teal'

interface PillProps {
  tone?: PillTone
  children: ReactNode
  /** A dot instead of a tinted background. For dense table cells. */
  subtle?: boolean
}

export function Pill({ tone = 'neutral', children, subtle = false }: PillProps) {
  return (
    <span
      className={[styles.pill, styles[tone], subtle ? styles.subtle : '']
        .filter(Boolean)
        .join(' ')}
    >
      {subtle && <span className={styles.dot} aria-hidden="true" />}
      {children}
    </span>
  )
}

/**
 * Status colour mapping, in one place.
 *
 * *** THE SAME STATUS MUST LOOK THE SAME EVERYWHERE ***
 * CONFIRMED appears on the dashboard, the bookings list, and a student's
 * detail page. Mapping colours per-screen guarantees they drift apart, and an
 * admin then learns the colours mean nothing.
 */
const BOOKING_TONES: Record<string, PillTone> = {
  CONFIRMED: 'success',
  COMPLETED: 'info',
  PENDING_PAYMENT: 'warning',
  CANCELLED: 'neutral',
  NO_SHOW: 'danger',
  NO_SHOW_FLAGGED: 'danger',
}

const PAYMENT_TONES: Record<string, PillTone> = {
  SUCCESS: 'success',
  CREATED: 'neutral',
  PENDING: 'warning',
  FAILED: 'danger',
  REFUNDED: 'info',
  PARTIALLY_REFUNDED: 'info',
}

const USER_TONES: Record<string, PillTone> = {
  ACTIVE: 'success',
  PROFILE_INCOMPLETE: 'warning',
  SUSPENDED: 'danger',
  PENDING_VERIFICATION: 'warning',
  DELETED: 'neutral',
}

function label(value: string): string {
  const spaced = value.replace(/_/g, ' ').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

export function BookingStatusPill({ status }: { status: string }) {
  return <Pill tone={BOOKING_TONES[status] ?? 'neutral'}>{label(status)}</Pill>
}

export function PaymentStatusPill({ status }: { status: string | null }) {
  if (!status) {
    // A booking can have no payment row at all — an abandoned checkout. That is
    // information, so it gets a pill rather than a blank cell.
    return <Pill tone="neutral">No payment</Pill>
  }
  return <Pill tone={PAYMENT_TONES[status] ?? 'neutral'}>{label(status)}</Pill>
}

export function UserStatusPill({ status }: { status: string }) {
  return <Pill tone={USER_TONES[status] ?? 'neutral'}>{label(status)}</Pill>
}

/**
 * Whether a mentor is visible to students.
 *
 * This answers the question an admin actually asks — "why can't students see
 * the mentor I created?" — so it says what to DO, not just what is true.
 */
export function VisibilityPill({
  isVisible,
  profileComplete,
}: {
  isVisible: boolean
  profileComplete: boolean
}) {
  if (isVisible) return <Pill tone="success">Visible</Pill>
  if (!profileComplete) return <Pill tone="warning">Profile incomplete</Pill>
  return <Pill tone="neutral">Hidden</Pill>
}
